import { CacheProvider } from '@emotion/react';
import React, { lazy, Suspense, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { CssBaseline } from '@mui/material';
import { AppLayout } from 'layouts';
import { IndexPage, NoMatch } from 'pages';
import { RoomPage } from 'features/room';
import { useAppSelector } from 'store/hooks';
import { selectBrandingOptions } from 'features/branding/brandingSlice';
import { createTheme } from 'theme';
import { useWindowHeight } from 'hooks/useWindowHeight';
import ErrorBoundaryFallback from 'components/ErrorBoundaryFallback';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import 'theme/common.css';
import 'theme/icons.css';

import 'react-resizable/css/styles.css';
import 'features/notifications/toast/toasts.css';
import { emotionCache } from 'utils/css';
import { resolveRoute } from 'appRoute';

const SystemCheckPage = lazy(() => import('features/system-check/SystemCheckPage'));
const SystemCheckReportPage = lazy(() => import('features/system-check/SystemCheckReportPage'));

const renderRoute = () => {
  switch (resolveRoute(window.location.pathname)) {
    case 'index':
      return <IndexPage />;
    case 'system-check':
      return <SystemCheckPage />;
    case 'system-check-report':
      return <SystemCheckReportPage />;
    case 'room':
      return <RoomPage />;
    default:
      return <NoMatch />;
  }
};

function App() {
  useWindowHeight();

  const brandingOptions = useAppSelector(selectBrandingOptions);

  const theme = useMemo(() => createTheme(brandingOptions), [brandingOptions]);

  return (
    <ThemeProvider theme={theme}>
      <CacheProvider value={emotionCache}>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />}>
            <Suspense fallback={null}>
              <AppLayout>{renderRoute()}</AppLayout>
            </Suspense>
          </Sentry.ErrorBoundary>
        </LocalizationProvider>
      </CacheProvider>
    </ThemeProvider>
  );
}

export default App;
